"use client";

import { motion } from "framer-motion";
import {
  ClipboardList,
  Languages,
  Headphones,
} from "lucide-react";

const useCases = [
  {
    icon: ClipboardList,
    title: "Survey & Research Recruitment",
    problem: "Recruiting respondents for surveys or studies is time-intensive.",
    solution: "AI agents call, screen, and recruit participants based on your criteria.",
    outcome: "Faster fieldwork completion and lower recruitment costs.",
  },
  {
    icon: Languages,
    title: "Multilingual Outbound Campaigns",
    problem: "Reaching diverse audiences requires agents fluent in multiple languages.",
    solution: "Deploy voice agents in dozens of languages without hiring multilingual staff.",
    outcome: "Broader reach, consistent messaging, and reduced staffing complexity.",
  },
  {
    icon: Headphones,
    title: "Overflow Support & After-Hours Coverage",
    problem: "Peak periods and off-hours leave customers waiting or unserved.",
    solution: "AI voice agents absorb overflow and handle calls 24/7.",
    outcome: "Uninterrupted customer coverage without overtime costs.",
  },
];

export default function UseCases() {
  return (
    <section
      id="use-cases"
      className="section-padding"
      style={{ background: "var(--color-bg-dark)" }}
    >
      <div className="section-inner">
        <h2 className="section-heading section-heading-light">
          Where market research firms and call center teams{" "}
          <em style={{ fontFamily: "var(--font-heading)" }}>see results</em>
        </h2>
        <p className="section-subheading section-subheading-light">
          Real problems. Automated voice solutions. Measurable business outcomes.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(min(340px, 100%), 1fr))",
            gap: 24,
          }}
        >
          {useCases.map((uc, i) => (
            <motion.div
              key={uc.title}
              className="card card-dark"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.07 }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 11,
                  background: "rgba(255,255,255,0.06)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 20,
                }}
              >
                <uc.icon size={20} color="rgba(255,255,255,0.85)" strokeWidth={1.6} />
              </div>
              <h3
                style={{
                  fontFamily: "var(--font-heading)",
                  fontSize: "1.2rem",
                  color: "#fff",
                  marginBottom: 16,
                }}
              >
                {uc.title}
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <p style={{ fontSize: "0.88rem", color: "rgba(255,255,255,0.55)", fontWeight: 300, lineHeight: 1.65 }}>
                  <strong style={{ color: "rgba(255,255,255,0.75)", fontWeight: 500 }}>Challenge:</strong>{" "}
                  {uc.problem}
                </p>
                <p style={{ fontSize: "0.88rem", color: "rgba(255,255,255,0.55)", fontWeight: 300, lineHeight: 1.65 }}>
                  <strong style={{ color: "rgba(255,255,255,0.75)", fontWeight: 500 }}>Solution:</strong>{" "}
                  {uc.solution}
                </p>
                <p style={{ fontSize: "0.88rem", color: "rgba(255,255,255,0.55)", fontWeight: 300, lineHeight: 1.65 }}>
                  <strong style={{ color: "rgba(255,255,255,0.75)", fontWeight: 500 }}>Outcome:</strong>{" "}
                  {uc.outcome}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
